/**
 * V-Berth Boat Cushion Configurator — Pricing Engine + Interactive SVG Preview
 * uscushion.com · 2026-08
 *
 * V-berth = symmetric trapezoid (Top Width < Bottom Width, Total Length = height).
 * Four configurations (customer receives N physically separate cushions):
 *   one   — One Piece    (1 cushion)                +$0
 *   twoA  — Two Piece A  (split down center, head gap)   +$15
 *   twoB  — Two Piece B  (A-frame, front-center notch)   +$15
 *   three — Three Piece  (A-frame + filler insert)       +$30
 *
 * Pricing model (confirmed 2026-08): base = outer trapezoid via bench formula
 * (effective width = max(topW, bottomW), length = totalL), PLUS a flat per-config
 * piece surcharge. Cutout dims are cut instructions only — they do NOT change base
 * price (we never undercharge on a removed notch, and labor is what multi-piece costs).
 *
 * Shared-input mapping (cushion-configurator-block.liquid provides 6 number inputs):
 *   cc-dim-l  → Top Width       cc-back-l → Total Length
 *   cc-dim-w  → Bottom Width     cc-back-w → Cutout Length     (twoA/twoB/three)
 *   cc-dim-t  → Thickness        cc-back-t → Cutout Top Width  (twoB/three)
 */
(function () {
  'use strict';

  /* ─── Cost constants (same as bench) ─── */
  var FABRIC_PRICE_RMB  = 25;
  var FABRIC_RATE_RMB   = { marine: 54 };
  function fabricRate() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return FABRIC_RATE_RMB[g] || FABRIC_PRICE_RMB;
  }
  var FOAM_PRICE_RMB    = 1750;
  var FOAM_DENSITY      = 35;
  var FABRIC_GSM        = 400;
  var SHIPPING_RMB      = 90;
  var ACCESSORIES_RMB   = 30;
  var EXCHANGE_RATE     = 6.8;
  var PAYMENT_FEE_RATE  = 0.049;
  var TARGET_COST_RATIO = 0.40;
  var MARINE_COST_RATIO = 0.38;
  var COVER_COST_RATIO  = 0.55;   /* Extra covers = 45% margin (AOV add-on, lower than cushion's 60/62%); same for standard & marine */
  function targetRatio() {
    var g = (state.fabric && state.fabric.tags) ? extractTag(state.fabric.tags, 'grade') : '';
    return g === 'marine' ? MARINE_COST_RATIO : TARGET_COST_RATIO;
  }
  var MIN_PRICE         = 45;

  /* ─── Piece configurations (flat surcharge per config) ─── */
  var PIECE = {
    one:   { label: 'One Piece',   count: 1, surcharge: 0  },
    twoA:  { label: 'Two Piece (A)', count: 2, surcharge: 15 },
    twoB:  { label: 'Two Piece (B)', count: 2, surcharge: 15 },
    three: { label: 'Three Piece', count: 3, surcharge: 30 }
  };
  function needsCutoutLength() { return state.pieces !== 'one'; }
  function needsCutoutTop()    { return state.pieces === 'twoB' || state.pieces === 'three'; }
  function needsCutoutBottom() { return state.pieces === 'twoB' || state.pieces === 'three'; }

  /* ─── Addon pricing (USD) ─── */
  var ADDON = {
    piping:      { none: 0, single: 5, double: 8 },
    pipingColor: { match: 0, white: 5, black: 5, navy: 5, cream: 5 },
    straps:      { none: 0, two: 2, four: 4 },
    zipper:      { regular: 0, invisible: 0 },
    antislip:    { none: 0, black_pad: 5, beige_pad: 5, velcro: 9 },
    throwPillow: {
      none: 0,
      '2pcs_12x20': 29, '2pcs_16x16': 29, '2pcs_18x18': 32,
      '2pcs_20x20': 35, '2pcs_24x24': 38, '2pcs_custom': 40,
      '4pcs_12x20': 51, '4pcs_16x16': 51, '4pcs_18x18': 57,
      '4pcs_20x20': 63, '4pcs_24x24': 67, '4pcs_custom': 70
    },
    pillowCovers: {
      none: 0,
      '2pcs_12x20': 19, '2pcs_16x16': 19, '2pcs_18x18': 21,
      '2pcs_20x20': 24, '2pcs_24x24': 29, '2pcs_custom': 31,
      '4pcs_12x20': 32, '4pcs_16x16': 32, '4pcs_18x18': 36,
      '4pcs_20x20': 42, '4pcs_24x24': 52, '4pcs_custom': 55
    },
    armrestPillow: { none: 0, square_2pcs: 30, round_2pcs: 30 }
  };

  var LABELS = {
    none: 'None', single: 'Single piping', double: 'Double piping',
    match: 'Match fabric', white: 'White piping', black: 'Black piping', navy: 'Navy piping', cream: 'Cream piping',
    two: '2 straps', four: '4 straps',
    regular: 'Regular zipper', invisible: 'Invisible zipper',
    velcro: 'Velcro', black_pad: 'Black pad', beige_pad: 'Beige pad'
  };

  var FABRIC_COLORS = {
    'blue':'#2c3e6b','navy':'#1a2540','teal':'#2a7a6b','green':'#4a7a2a',
    'sage':'#a8b89a','red':'#b83232','coral':'#d85a45','orange':'#d4783a',
    'yellow':'#d4a83a','gold':'#c8963a','pink':'#d4649a','purple':'#6a4a9a',
    'lavender':'#9a8ab8','brown':'#7a5a3a','camel':'#c8a878','tan':'#c8b08a',
    'beige':'#d4c8a8','cream':'#ede8df','ivory':'#f0ead8','white':'#f5f5f0',
    'gray':'#9a9a9a','grey':'#9a9a9a','charcoal':'#4a4a4a','black':'#1a1a1a',
    'mint':'#98d4b4','rust':'#b7502a','mustard':'#c8a020','olive':'#6b702a',
    'blush':'#de9898','mauve':'#b07898','burgundy':'#6b1a2a','taupe':'#8a7a6a',
    'sand':'#c8b898','silver':'#bfc0c2','steel blue':'#4a7a98',
    'dark olive':'#4a4a2a','dark green':'#2a5a2a','dark brown':'#4a3020',
    'dark blue':'#1a2a5a','dark teal':'#1a5a5a','dark gray':'#5a5a5a',
    'light gray':'#c8c8c8','gray blue':'#6a7a8a','yellow green':'#8ab82a',
    'gray black':'#3a3a3a'
  };

  /* ─── State ─── */
  var state = {
    pieces: 'one',
    topW: 0, bottomW: 0, totalL: 0, thickness: 0,
    cutoutLength: 0, cutoutTopWidth: 0, cutoutBottomWidth: 0,
    fabric: null, piping: '', pipingColor: 'match', straps: '',
    zipper: 'regular', zipperPlacement: '', antislip: '',
    extraCovers: '0', throwPillow: 'none', pillowCovers: 'none',
    armrestPillow: 'none', notes: ''
  };

  var els = {};

  /* ─── Color helpers ─── */
  function hexToRgb(hex) {
    hex = hex.replace('#', '');
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)];
  }
  function rgbToHex(r, g, b) {
    return '#' + [r, g, b].map(function (c) {
      var x = Math.round(Math.max(0, Math.min(255, c))).toString(16);
      return x.length === 1 ? '0' + x : x;
    }).join('');
  }
  function lighten(hex, r) { var c = hexToRgb(hex); return rgbToHex(c[0] + (255 - c[0]) * r, c[1] + (255 - c[1]) * r, c[2] + (255 - c[2]) * r); }
  function darken(hex, r)  { var c = hexToRgb(hex); return rgbToHex(c[0] * (1 - r), c[1] * (1 - r), c[2] * (1 - r)); }
  function extractTag(tags, prefix) {
    if (!tags) return '';
    for (var i = 0; i < tags.length; i++) {
      var t = (typeof tags[i] === 'string' ? tags[i] : '').toLowerCase().trim();
      if (t.indexOf(prefix + ':') === 0) return tags[i].split(':')[1].trim().toLowerCase();
    }
    return '';
  }

  /* ─── Pricing (bench formula; L & W are interchangeable in the formula) ─── */
  function calcBasePrice(L, W, T) {
    if (!L || !W || !T) return null;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var foamVolume = Lm * Wm * Tm;
    var totalWeight = foamVolume * FOAM_DENSITY + fabricArea * FABRIC_GSM / 1000;
    var totalRMB = fabricArea * fabricRate() + foamVolume * FOAM_PRICE_RMB + totalWeight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.max(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / targetRatio() * (window.__CC_COVER_FACTOR || 1), MIN_PRICE);
  }

  /* One extra cover = a fabric-only shell (no foam). V-berth bounding rectangle
     = totalL × max(topW, bottomW), matching calcTrapBase. Fabric grade auto via
     fabricRate, at COVER_COST_RATIO (45% margin). Replaces the flat $30/$50 fee. */
  function extraCoverUnit() {
    var L = state.totalL, W = Math.max(state.topW, state.bottomW), T = state.thickness;
    if (!L || !W || !T) return 0;
    var Lm = L / 39.37, Wm = W / 39.37, Tm = T / 39.37;
    var fabricArea = Lm * Wm * 2 + Lm * Tm * 2 + Wm * Tm * 2;
    var weight = fabricArea * FABRIC_GSM / 1000; // fabric weight only — no foam
    var totalRMB = fabricArea * fabricRate() + weight * SHIPPING_RMB + ACCESSORIES_RMB;
    return Math.round(totalRMB / EXCHANGE_RATE / (1 - PAYMENT_FEE_RATE) / COVER_COST_RATIO);
  }

  function updateExtraCoverLabels() {
    if (!els.extraCovers) return;
    var unit = extraCoverUnit();
    var opts = els.extraCovers.options;
    for (var i = 0; i < opts.length; i++) {
      var n = parseInt(opts[i].value) || 0;
      if (n === 0) { opts[i].text = 'No extra cover'; continue; }
      var label = (n === 1 ? '1 set' : n + ' sets');
      opts[i].text = unit > 0 ? label + ' (+$' + (unit * n) + ')' : label;
    }
  }

  function calcTrapBase() {
    var effW = Math.max(state.topW, state.bottomW);
    return calcBasePrice(state.totalL, effW, state.thickness);
  }
  function pieceSurcharge() {
    return (PIECE[state.pieces] || PIECE.one).surcharge;
  }

  function getAddonTotal() {
    return (ADDON.piping[state.piping] || 0)
      + (ADDON.pipingColor[state.pipingColor] || 0)
      + (ADDON.straps[state.straps] || 0) + (ADDON.zipper[state.zipper] || 0)
      + (ADDON.antislip[state.antislip] || 0) + extraCoverUnit() * (parseInt(state.extraCovers) || 0)
      + (ADDON.throwPillow[state.throwPillow] || 0)
      + (ADDON.pillowCovers[state.pillowCovers] || 0)
      + (ADDON.armrestPillow[state.armrestPillow] || 0);
  }

  function hasDims() {
    if (!(state.topW >= 6 && state.bottomW >= 6 && state.totalL >= 6 && state.thickness >= 1)) return false;
    if (needsCutoutLength() && !(state.cutoutLength >= 1)) return false;
    if (needsCutoutTop() && !(state.cutoutTopWidth >= 1)) return false;
    if (needsCutoutBottom() && !(state.cutoutBottomWidth >= 1)) return false;
    return true;
  }

  function isComplete() {
    return hasDims() && state.pieces
      && state.fabric && state.piping && state.straps && state.antislip;
  }

  /* ═══════════════════════════════════════════════════════════
     SVG Preview — isometric extruded trapezoid(s)
     Plan coords: x = width (centered on 0), y = depth (0 = front/bottom wide edge,
     increasing toward the narrow top edge at y = totalL). Same iso axes as RV.
     ═══════════════════════════════════════════════════════════ */
  function drawCushion() {
    var svg = els.dimSvg;
    if (!svg) return;

    function clamp(v, mn, mx) { return Math.max(mn, Math.min(mx, v)); }
    function pt(a) { return a[0].toFixed(1) + ',' + a[1].toFixed(1); }
    function f(n)  { return n.toFixed(1); }

    var TW = state.topW, BW = state.bottomW, TL = state.totalL, TK = state.thickness;
    var CL = state.cutoutLength, CTW = state.cutoutTopWidth, CBW = state.cutoutBottomWidth;
    var empty = (!TW && !BW && !TL && !TK);

    /* effective (fall back to a sensible WIDE demo shape so preview is never blank) */
    var uTW = TW || 44, uBW = BW || 76, uTL = TL || 54, uTK = TK || 4;
    var uCL = CL || Math.round(uTL * 0.55);
    var uCTW = clamp(CTW || Math.round(uBW * 0.20), 3, uBW * 0.8);
    var uCBW = clamp(CBW || Math.round(uBW * 0.40), uCTW, uBW * 0.9);
    if (uCL > uTL) uCL = uTL;

    /* ── Face colors from fabric ── */
    var topC = '#EDE9E0', frontC = '#D5D0C2', sideC = '#E4E0D4', strokeC = '#2A2A28';
    if (state.fabric && state.fabric.tags) {
      var colorTag = extractTag(state.fabric.tags, 'color');
      var baseHex = FABRIC_COLORS[colorTag];
      if (baseHex) {
        topC = lighten(baseHex, 0.50);
        sideC = lighten(baseHex, 0.35);
        frontC = lighten(baseHex, 0.20);
        strokeC = darken(baseHex, 0.45);
        var sc = hexToRgb(strokeC);
        if ((sc[0] + sc[1] + sc[2]) / 3 > 120) strokeC = '#2A2A28';
      }
    }

    var dirLen   = [0.98776, -0.15596];   // width axis (x)
    var dirWidth = [-0.35120, -0.93660];   // depth axis (y, front → back)

    /* Base scale (true-ish iso), then widen by 1/3 and shorten length by 1/3
       so the preview reads wide & shallow like a real V-berth. */
    var sBase = clamp(300 / Math.max(uBW, uTW, uTL), 1.1, 6);
    var sx = sBase * 1.34;   // width  +1/3
    var sy = sBase * 0.66;   // length −1/3
    var seatH = clamp(uTK * 6, 8, 40);
    var gapPx = 14;                                       // visual gap between separate pieces
    var base = [140, 250];

    function proj(x, y, sh) {
      return [
        base[0] + dirLen[0] * (x * sx) + dirWidth[0] * (y * sy) + (sh ? sh[0] : 0),
        base[1] + dirLen[1] * (x * sx) + dirWidth[1] * (y * sy) + (sh ? sh[1] : 0)
      ];
    }

    /* ── Outer trapezoid plan corners (centered) ── */
    var half = { bw: uBW / 2, tw: uTW / 2 };
    var notchHalf = uCTW / 2;

    /* ── Build piece list: each = { outline:[[x,y]...], sh:[dx,dy], role } ── */
    var pieces = [];
    var shR = [dirLen[0] * gapPx, dirLen[1] * gapPx];        // right piece shift
    var shL = [-shR[0], -shR[1]];                            // left piece shift

    if (state.pieces === 'one') {
      pieces.push({ outline: [[-half.bw, 0], [half.bw, 0], [half.tw, uTL], [-half.tw, uTL]], sh: [0, 0] });
    } else if (state.pieces === 'twoA') {
      /* trapezoid cut straight down the center into two clean right-trapezoid halves
         (flat top, full vertical inner seam). Cutout Length just labels the seam
         portion where the filler goes — it does not carve a notch. */
      pieces.push({ outline: [[0, 0], [half.bw, 0], [half.tw, uTL], [0, uTL]], sh: shR });
      pieces.push({ outline: [[0, 0], [-half.bw, 0], [-half.tw, uTL], [0, uTL]], sh: shL });
    } else if (state.pieces === 'twoB') {
      /* = Two Piece A (two halves meeting along the center seam) with a trapezoidal
         notch carved out of the bottom center: top width = Cutout Top Width,
         bottom width = Cutout Bottom Width, notch top at y = TL − Cutout Length
         (Cutout Length = the still-joined center seam above the notch). */
      var ctwH = uCTW / 2, cbwH = uCBW / 2, nTop = uTL - uCL;
      pieces.push({ outline: [[0, uTL], [half.tw, uTL], [half.bw, 0], [cbwH, 0], [ctwH, nTop], [0, nTop]], sh: [0, 0] });
      pieces.push({ outline: [[0, uTL], [-half.tw, uTL], [-half.bw, 0], [-cbwH, 0], [-ctwH, nTop], [0, nTop]], sh: [0, 0] });
    } else if (state.pieces === 'three') {
      var ctwH3 = uCTW / 2, cbwH3 = uCBW / 2, nTop3 = uTL - uCL;
      pieces.push({ outline: [[0, uTL], [half.tw, uTL], [half.bw, 0], [cbwH3, 0], [ctwH3, nTop3], [0, nTop3]], sh: [0, 0] });
      pieces.push({ outline: [[0, uTL], [-half.tw, uTL], [-half.bw, 0], [-cbwH3, 0], [-ctwH3, nTop3], [0, nTop3]], sh: [0, 0] });
      /* filler insert = the carved-out notch trapezoid, pulled toward viewer as the 3rd piece */
      pieces.push({ outline: [[-cbwH3, 0], [cbwH3, 0], [ctwH3, nTop3], [-ctwH3, nTop3]], sh: [0, 30], role: 'insert' });
    }

    /* ── Extrude + draw one piece ── */
    function drawPiece(piece) {
      var top = piece.outline.map(function (p) { return proj(p[0], p[1], piece.sh); });
      var bot = top.map(function (p) { return [p[0], p[1] + seatH]; });
      var cx = 0, cy = 0;
      top.forEach(function (p) { cx += p[0]; cy += p[1]; });
      cx /= top.length; cy /= top.length;

      /* Draw EVERY side wall (incl. concave notch walls) so the cutout shows its
         thickness — no back-face culling, which would leave the notch "broken".
         Order walls far→near (by their lowest screen point); the top face is drawn
         last and covers any wall that belongs behind it. */
      var walls = [];
      for (var i = 0; i < top.length; i++) {
        var j = (i + 1) % top.length;
        var mx = (top[i][0] + top[j][0]) / 2, my = (top[i][1] + top[j][1]) / 2;
        var nx = mx - cx, ny = my - cy;
        var nl = Math.sqrt(nx * nx + ny * ny) || 1;
        var depth = Math.max(top[i][1], top[j][1]) + seatH;   // nearer = larger screen y
        walls.push({ d: depth, quad: [top[i], top[j], bot[j], bot[i]], front: (ny / nl > 0.4) });
      }
      walls.sort(function (a, b) { return a.d - b.d; });   // painter: far first

      var out = '';
      walls.forEach(function (w) {
        out += '<polygon points="' + w.quad.map(pt).join(' ') + '" fill="' + (w.front ? frontC : sideC)
          + '" stroke="' + strokeC + '" stroke-width="1.3" stroke-linejoin="round"/>';
      });
      out += '<polygon points="' + top.map(pt).join(' ') + '" fill="' + topC
        + '" stroke="' + strokeC + '" stroke-width="1.4" stroke-linejoin="round"/>';
      return { html: out, top: top, bot: bot };
    }

    var h = '';
    h += '<defs><marker id="vb-arr" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">'
      + '<path d="M1 1.5L8.5 5L1 8.5" fill="none" stroke="context-stroke" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></marker></defs>';

    var allPts = [];
    var drawn = pieces.map(function (pc) {
      var d = drawPiece(pc);
      allPts = allPts.concat(d.top, d.bot);
      return d;
    });
    /* draw insert last so it sits in front */
    drawn.forEach(function (d, i) { if (pieces[i].role !== 'insert') h += d.html; });
    drawn.forEach(function (d, i) { if (pieces[i].role === 'insert') h += d.html; });

    /* ── Auto-fit viewBox ── */
    var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
    allPts.forEach(function (p) {
      if (p[0] < minX) minX = p[0]; if (p[0] > maxX) maxX = p[0];
      if (p[1] < minY) minY = p[1]; if (p[1] > maxY) maxY = p[1];
    });
    var mrgL = 152, mrgR = 80, mrgT = 50, mrgB = 124;
    svg.setAttribute('viewBox', Math.floor(minX - mrgL) + ' ' + Math.floor(minY - mrgT) + ' '
      + Math.ceil(maxX - minX + mrgL + mrgR) + ' ' + Math.ceil(maxY - minY + mrgT + mrgB));

    /* ── Dimension labels (large, high-contrast so customers can read them) ── */
    var dimC = '#1a4f78', thkC = '#3d5e3f', cutC = '#8a4a1a', ls = 13;
    function dim(a, b, txt, color, off, txtOff, anchor, rotate) {
      var a2 = [a[0] + off[0], a[1] + off[1]], b2 = [b[0] + off[0], b[1] + off[1]];
      var o = '';
      o += '<line x1="' + f(a[0]) + '" y1="' + f(a[1]) + '" x2="' + f(a2[0]) + '" y2="' + f(a2[1]) + '" stroke="' + color + '" stroke-width="0.9"/>';
      o += '<line x1="' + f(b[0]) + '" y1="' + f(b[1]) + '" x2="' + f(b2[0]) + '" y2="' + f(b2[1]) + '" stroke="' + color + '" stroke-width="0.9"/>';
      o += '<line x1="' + f(a2[0]) + '" y1="' + f(a2[1]) + '" x2="' + f(b2[0]) + '" y2="' + f(b2[1])
        + '" stroke="' + color + '" stroke-width="1.9" marker-start="url(#vb-arr)" marker-end="url(#vb-arr)"/>';
      var mx = (a2[0] + b2[0]) / 2 + txtOff[0], my = (a2[1] + b2[1]) / 2 + txtOff[1];
      var trans = '';
      if (rotate) {
        var ang = Math.atan2(b2[1] - a2[1], b2[0] - a2[0]) * 180 / Math.PI;
        if (ang > 90) ang -= 180; else if (ang < -90) ang += 180;   // keep upright
        trans = ' transform="rotate(' + ang.toFixed(1) + ' ' + f(mx) + ' ' + f(my) + ')"';
      }
      o += '<text x="' + f(mx) + '" y="' + f(my) + '"' + trans + ' text-anchor="' + (anchor || 'middle') + '" font-size="' + ls
        + '" font-family="Poppins,sans-serif" font-weight="700" fill="' + color + '">' + txt + '</text>';
      return o;
    }

    /* anchor points from raw projection (outer corners). Only twoA offsets its pieces. */
    var lSh = (state.pieces === 'twoA') ? shL : [0, 0];
    var rSh = (state.pieces === 'twoA') ? shR : [0, 0];
    var pFLb = proj(-half.bw, 0, lSh); pFLb = [pFLb[0], pFLb[1] + seatH];
    var pFRb = proj(half.bw, 0, rSh); pFRb = [pFRb[0], pFRb[1] + seatH];
    var pBL  = proj(-half.tw, uTL, lSh);
    var pBR  = proj(half.tw, uTL, rSh);
    var pFRt = proj(half.bw, 0, rSh);
    var pFRtb = [pFRt[0], pFRt[1] + seatH];

    var lblTW = TW ? ' — ' + TW + '″' : '';
    var lblBW = BW ? ' — ' + BW + '″' : '';
    var lblTL = TL ? ' — ' + TL + '″' : '';
    var lblTK = TK ? ' — ' + TK + '″' : '';

    h += dim(pFLb, pFRb, 'Bottom Width' + lblBW, dimC, [0, 66], [0, 18]);
    h += dim(pBL, pBR, 'Top Width' + lblTW, dimC, [0, -30], [0, -6]);
    h += dim(pFLb, pBL, 'Total Length' + lblTL, dimC, [-30, -6], [-8, 0], 'end');
    /* Thickness — right vertical */
    var thx = pFRt[0] + 32;
    h += '<line x1="' + f(pFRt[0]) + '" y1="' + f(pFRt[1]) + '" x2="' + f(thx) + '" y2="' + f(pFRt[1]) + '" stroke="' + thkC + '" stroke-width="0.9"/>';
    h += '<line x1="' + f(pFRtb[0]) + '" y1="' + f(pFRtb[1]) + '" x2="' + f(thx) + '" y2="' + f(pFRtb[1]) + '" stroke="' + thkC + '" stroke-width="0.9"/>';
    h += '<line x1="' + f(thx) + '" y1="' + f(pFRt[1] + 2) + '" x2="' + f(thx) + '" y2="' + f(pFRtb[1] - 2) + '" stroke="' + thkC + '" stroke-width="1.9" marker-start="url(#vb-arr)" marker-end="url(#vb-arr)"/>';
    h += '<text x="' + f(thx + 7) + '" y="' + f((pFRt[1] + pFRtb[1]) / 2 + 5) + '" font-size="' + ls + '" font-family="Poppins,sans-serif" font-weight="700" fill="' + thkC + '">T' + lblTK + '</text>';

    /* Cutout labels */
    var nTopY = uTL - uCL;               // notch top height (twoB / three)
    var ctwHl = uCTW / 2, cbwHl = uCBW / 2;
    if (needsCutoutLength()) {
      var lblCL = CL ? ' — ' + CL + '″' : '';
      if (state.pieces === 'twoA') {
        /* full inner seam, label offset right into the gap */
        var ia = proj(0, 0, [0, 0]), ib = proj(0, uTL, [0, 0]);
        h += dim(ia, ib, 'Cutout Length' + lblCL, cutC, [0, 0], [8, 2], 'start');
      } else {
        /* joined center seam above the notch — label offset right so it clears Cutout Top Width */
        var clA = proj(0, nTopY, [0, 0]), clB = proj(0, uTL, [0, 0]);
        h += dim(clA, clB, 'Cutout Length' + lblCL, cutC, [0, 0], [10, 2], 'start');
      }
    }
    if (needsCutoutTop()) {
      var lblCTW = CTW ? ' — ' + CTW + '″' : '';
      /* width across the top of the notch (y = TL − CL); leader up-left to clear Cutout Length */
      var tL = proj(-ctwHl, nTopY, [0, 0]), tR = proj(ctwHl, nTopY, [0, 0]);
      h += dim(tL, tR, 'Cutout Top Width' + lblCTW, cutC, [-26, -16], [0, -6], 'end');
    }
    if (needsCutoutBottom()) {
      var lblCBW = CBW ? ' — ' + CBW + '″' : '';
      /* width across the bottom of the notch (y = 0); sits just under the notch, above Bottom Width */
      var bL = proj(-cbwHl, 0, [0, 0]), bR = proj(cbwHl, 0, [0, 0]);
      var bLb = [bL[0], bL[1] + seatH], bRb = [bR[0], bR[1] + seatH];
      h += dim(bLb, bRb, 'Cutout Bottom Width' + lblCBW, cutC, [0, 34], [0, 22]);
    }

    if (empty) {
      h += '<text x="' + f(base[0]) + '" y="' + f(maxY + 100) + '" text-anchor="middle" font-size="14" font-family="Poppins,sans-serif" fill="#8A8A85">Enter dimensions below to see your cushion</text>';
    }

    svg.innerHTML = h;
  }

  /* ─── Summary ─── */
  function updateSummary() {
    if (!els.summary) return;
    var parts = [];
    parts.push((PIECE[state.pieces] || PIECE.one).label);
    if (state.topW && state.bottomW && state.totalL && state.thickness) {
      var d = state.topW + '×' + state.bottomW + '×' + state.totalL + '×' + state.thickness + '″';
      parts.push(d);
    }
    if (state.fabric) parts.push(state.fabric.title);
    if (state.piping && state.piping !== 'none') {
      var pipLabel = LABELS[state.piping];
      if (state.pipingColor && state.pipingColor !== 'match') pipLabel += ' (' + (LABELS[state.pipingColor] || state.pipingColor) + ')';
      parts.push(pipLabel);
    }
    if (state.straps && state.straps !== 'none') parts.push(LABELS[state.straps]);
    if (state.antislip && state.antislip !== 'none') parts.push(LABELS[state.antislip]);
    els.summary.textContent = parts.length ? parts.join(' · ') : 'Configure your V-berth cushion below';
  }

  /* ─── Field visibility by config ─── */
  function applyFieldVisibility() {
    if (els.cutoutLength) {
      var clF = els.cutoutLength.closest('.cc-field');
      if (clF) clF.style.display = needsCutoutLength() ? '' : 'none';
    }
    if (els.cutoutTopWidth) {
      var ctF = els.cutoutTopWidth.closest('.cc-field');
      if (ctF) ctF.style.display = needsCutoutTop() ? '' : 'none';
    }
    if (els.cutoutBottomWidth) {
      var cbF = els.cutoutBottomWidth.closest('.cc-field');
      if (cbF) cbF.style.display = needsCutoutBottom() ? '' : 'none';
    }
  }

  /* ─── Recalc ─── */
  function recalc() {
    var _cf = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (_cf && _cf !== state.fabric) state.fabric = _cf;

    state.topW = parseFloat(els.topW.value) || 0;
    state.bottomW = parseFloat(els.bottomW.value) || 0;
    state.totalL = parseFloat(els.totalL.value) || 0;
    state.thickness = parseFloat(els.thickness.value) || 0;
    state.cutoutLength = els.cutoutLength ? (parseFloat(els.cutoutLength.value) || 0) : 0;
    state.cutoutTopWidth = els.cutoutTopWidth ? (parseFloat(els.cutoutTopWidth.value) || 0) : 0;
    state.cutoutBottomWidth = els.cutoutBottomWidth ? (parseFloat(els.cutoutBottomWidth.value) || 0) : 0;
    state.extraCovers = els.extraCovers.value;
    state.throwPillow = els.throwPillow ? els.throwPillow.value : 'none';
    state.pillowCovers = els.pillowCovers ? els.pillowCovers.value : 'none';
    state.armrestPillow = els.armrestPillow ? els.armrestPillow.value : 'none';
    state.zipperPlacement = els.zipperPlacement ? els.zipperPlacement.value : '';
    state.pipingColor = els.pipingColor ? els.pipingColor.value : 'match';
    state.notes = els.notes.value;

    if (els.pipingColorWrap) {
      var showPipingColor = (state.piping === 'single' || state.piping === 'double');
      els.pipingColorWrap.style.display = showPipingColor ? '' : 'none';
      if (!showPipingColor) {
        state.pipingColor = 'match';
        if (els.pipingColor) els.pipingColor.value = 'match';
      }
    }

    applyFieldVisibility();

    ['topW', 'bottomW', 'totalL', 'thickness'].forEach(function (id) {
      els[id].classList.toggle('ok', parseFloat(els[id].value) > 0);
    });
    if (els.cutoutLength) els.cutoutLength.classList.toggle('ok', parseFloat(els.cutoutLength.value) > 0);
    if (els.cutoutTopWidth) els.cutoutTopWidth.classList.toggle('ok', parseFloat(els.cutoutTopWidth.value) > 0);
    if (els.cutoutBottomWidth) els.cutoutBottomWidth.classList.toggle('ok', parseFloat(els.cutoutBottomWidth.value) > 0);

    drawCushion();
    updateSummary();

    var dimOk = hasDims();
    var ec = parseInt(state.extraCovers) || 0;
    updateExtraCoverLabels();
    var base = dimOk ? calcTrapBase() : null;
    var surcharge = pieceSurcharge();
    var addonTotal = getAddonTotal();

    if (base !== null) {
      var finalPrice = Math.round(base + surcharge + addonTotal);
      els.priceMain.textContent = '$' + finalPrice;
      var parts = ['Base $' + Math.round(base)];
      if (surcharge) parts.push((PIECE[state.pieces].count) + '-piece +$' + surcharge);
      if (ADDON.piping[state.piping])     parts.push('Piping +$' + ADDON.piping[state.piping]);
      if (ADDON.pipingColor[state.pipingColor]) parts.push('Piping color +$' + ADDON.pipingColor[state.pipingColor]);
      if (ADDON.straps[state.straps])     parts.push('Straps +$' + ADDON.straps[state.straps]);
      if (ADDON.antislip[state.antislip]) parts.push('Anti-slip +$' + ADDON.antislip[state.antislip]);
      if (ec > 0)                         parts.push('Covers +$' + (extraCoverUnit() * ec));
      if (ADDON.throwPillow[state.throwPillow])   parts.push('Pillow +$' + ADDON.throwPillow[state.throwPillow]);
      if (ADDON.pillowCovers[state.pillowCovers]) parts.push('P.Covers +$' + ADDON.pillowCovers[state.pillowCovers]);
      if (ADDON.armrestPillow[state.armrestPillow]) parts.push('Armrest +$' + ADDON.armrestPillow[state.armrestPillow]);
      els.priceBreak.textContent = parts.join(' · ');
    } else {
      els.priceMain.textContent = '$—';
      els.priceBreak.textContent = 'Enter dimensions to see price';
    }

    var complete = isComplete();
    var btns = [els.addBtn, els.addBtnSticky].filter(Boolean);
    btns.forEach(function (btn) {
      btn.disabled = !complete;
      if (complete && base !== null) {
        btn.textContent = 'Add to order — $' + Math.round(base + surcharge + addonTotal) + ' · Free shipping →';
      } else {
        btn.textContent = 'Add to order →';
      }
    });

    if (els.addNote) {
      if (complete) {
        els.addNote.textContent = 'Free shipping · Ships in ~20 business days';
      } else {
        var missing = [];
        if (!dimOk) missing.push('dimensions');
        if (!state.fabric) missing.push('fabric');
        if (!state.piping) missing.push('piping');
        if (!state.straps) missing.push('straps');
        if (!state.antislip) missing.push('anti-slip');
        els.addNote.textContent = missing.length ? 'Select ' + missing.join(', ') + ' to continue' : '';
      }
    }

    document.dispatchEvent(new CustomEvent('configurator:price-updated', {
      detail: {
        price: base !== null ? Math.round(base + surcharge + addonTotal) : null,
        state: state
      }
    }));
  }

  /* ─── Card selection ─── */
  function pickCard(el, group, value) {
    el.parentElement.querySelectorAll('.cc-opt-card').forEach(function (c) { c.classList.remove('sel'); });
    el.classList.add('sel');
    state[group] = value;
    recalc();
  }

  /* ─── Line properties for cart ─── */
  function getLineProperties() {
    var ec = parseInt(state.extraCovers) || 0;
    var base = calcTrapBase();
    var surcharge = pieceSurcharge();
    var finalPrice = Math.round(base + surcharge + getAddonTotal());
    var cfg = PIECE[state.pieces] || PIECE.one;

    var props = {
      'Shape': 'V-Berth Boat Cushion',
      'Configuration': cfg.label + (surcharge ? ' (+$' + surcharge + ')' : ''),
      'Top Width': state.topW + '"',
      'Bottom Width': state.bottomW + '"',
      'Total Length': state.totalL + '"',
      'Thickness': state.thickness + '"'
    };
    if (needsCutoutLength()) props['Cutout Length'] = state.cutoutLength + '"';
    if (needsCutoutTop()) props['Cutout Top Width'] = state.cutoutTopWidth + '"';
    if (needsCutoutBottom()) props['Cutout Bottom Width'] = state.cutoutBottomWidth + '"';
    props['Piping'] = LABELS[state.piping] || state.piping;
    props['Piping Color'] = (state.pipingColor && state.pipingColor !== 'match') ? (LABELS[state.pipingColor] || state.pipingColor) : 'Match fabric';
    props['Straps'] = LABELS[state.straps] || state.straps;
    props['Zipper'] = LABELS[state.zipper] || state.zipper;
    props['Anti-slip'] = LABELS[state.antislip] || state.antislip;

    if (state.fabric) {
      props['Fabric'] = state.fabric.title || '';
      props['_Fabric SKU'] = (state.fabric.variants && state.fabric.variants[0] && state.fabric.variants[0].sku) || '';
      props['_Fabric Handle'] = state.fabric.handle || '';
      props['_Fabric Image'] = (state.fabric.images && state.fabric.images[0] && state.fabric.images[0].src) || '';
      var _grade = extractTag(state.fabric.tags || [], 'grade');
      if (_grade) props['Fabric Grade'] = _grade.charAt(0).toUpperCase() + _grade.slice(1);
    }
    if (ec > 0) props['Extra Covers'] = ec + ' set(s) (+$' + (extraCoverUnit() * ec) + ')';
    if (state.throwPillow && state.throwPillow !== 'none')
      props['Throw Pillow'] = state.throwPillow.replace(/_/g, ' ') + ' (+$' + ADDON.throwPillow[state.throwPillow] + ')';
    if (state.pillowCovers && state.pillowCovers !== 'none')
      props['Pillow Covers'] = state.pillowCovers.replace(/_/g, ' ') + ' (+$' + ADDON.pillowCovers[state.pillowCovers] + ')';
    if (state.armrestPillow && state.armrestPillow !== 'none')
      props['Armrest Pillow'] = state.armrestPillow.replace(/_/g, ' ') + ' (+$' + ADDON.armrestPillow[state.armrestPillow] + ')';
    if (state.zipperPlacement) {
      props['Zipper Placement'] = state.zipperPlacement;
    }
    if (state.notes.trim()) props['Customer Note'] = state.notes.trim();
    props['_Source Product'] = (document.getElementById('cc-configurator') || { dataset: {} }).dataset.productTitle || document.title.split('–')[0].split('—')[0].trim() || location.pathname;

    return { properties: props, price: finalPrice };
  }

  /* ─── Shape-card thumbnails (inline SVG, self-contained) ─── */
  function thumb(paths) {
    return '<svg viewBox="0 0 60 60" width="100%" height="100%" xmlns="http://www.w3.org/2000/svg" style="display:block">'
      + paths + '</svg>';
  }
  var FILL = 'fill="#12223a"';
  var THUMBS = {
    one:   thumb('<path d="M18 8 H42 L52 52 H8 Z" ' + FILL + '/>'),
    twoA:  thumb('<path d="M18 8 H28 L25 52 H8 Z" ' + FILL + '/><path d="M32 8 H42 L52 52 H35 Z" ' + FILL + '/>'),
    twoB:  thumb('<path d="M18 8 H42 L52 52 H36 L33 32 H27 L24 52 H8 Z" ' + FILL + '/>'),
    three: thumb('<path d="M18 8 H42 L52 52 H36 L33 32 H27 L24 52 H8 Z" ' + FILL + '/><path d="M25 54 H35 L33 42 H27 Z" ' + FILL + '/>')
  };

  /* ─── DOM transformation (adapt shared Liquid section for V-Berth) ─── */
  function transformDOM() {
    var svg = document.getElementById('cc-dim-svg');
    if (svg) svg.style.maxHeight = '260px';

    /* Replace Shape cards with the 4 configurations */
    var shapeGrid = document.getElementById('cc-shape-grid');
    if (shapeGrid) {
      shapeGrid.innerHTML = ''
        + card('one', 'One Piece', 'included')
        + card('twoA', 'Two Piece (A)', '+$15')
        + card('twoB', 'Two Piece (B)', '+$15')
        + card('three', 'Three Piece', '+$30');
    }
    var shapeLabel = document.querySelector('#cc-shape-section .cc-sl');
    if (shapeLabel) shapeLabel.innerHTML = 'Configuration <span class="cc-req">*</span>';

    function card(val, name, price) {
      return '<div class="cc-opt-card" data-group="pieces" data-value="' + val + '">'
        + '<div class="cc-opt-img" style="padding:10px">' + THUMBS[val] + '</div>'
        + '<div class="cc-opt-name">' + name + '</div><div class="cc-opt-price">' + price + '</div></div>';
    }

    /* Seat row → Top Width / Bottom Width / Thickness */
    var labelL = document.querySelector('label[for="cc-dim-l"]');
    if (labelL) labelL.innerHTML = 'Top Width (in) <span class="cc-req">*</span>';
    var inputL = document.getElementById('cc-dim-l');
    if (inputL) { inputL.placeholder = 'e.g. 40'; inputL.max = '120'; }

    var labelW = document.querySelector('label[for="cc-dim-w"]');
    if (labelW) labelW.innerHTML = 'Bottom Width (in) <span class="cc-req">*</span>';
    var inputW = document.getElementById('cc-dim-w');
    if (inputW) { inputW.placeholder = 'e.g. 64'; inputW.max = '120'; }

    var labelT = document.querySelector('label[for="cc-dim-t"]');
    if (labelT) labelT.innerHTML = 'Thickness (in) <span class="cc-req">*</span>';

    /* Back row → Total Length / Cutout Length / Cutout Top Width */
    var backDims = document.querySelectorAll('.cc-back-dims');
    for (var i = 0; i < backDims.length; i++) {
      var el = backDims[i];
      el.style.display = el.classList.contains('cc-sp-dims') ? 'grid' : 'block';
    }
    var labelBL = document.querySelector('label[for="cc-back-l"]');
    if (labelBL) labelBL.innerHTML = 'Total Length (in) <span class="cc-req">*</span>';
    var inputBL = document.getElementById('cc-back-l');
    if (inputBL) { inputBL.placeholder = 'e.g. 78'; inputBL.max = '120'; }

    var labelBW = document.querySelector('label[for="cc-back-w"]');
    if (labelBW) labelBW.innerHTML = 'Cutout Length (in) <span class="cc-req">*</span>';
    var inputBW = document.getElementById('cc-back-w');
    if (inputBW) { inputBW.placeholder = 'e.g. 36'; inputBW.max = '120'; inputBW.min = '1'; }

    var labelBT = document.querySelector('label[for="cc-back-t"]');
    if (labelBT) labelBT.innerHTML = 'Cutout Top Width (in) <span class="cc-req">*</span>';
    var inputBT = document.getElementById('cc-back-t');
    if (inputBT) { inputBT.placeholder = 'e.g. 16'; inputBT.max = '120'; inputBT.min = '1'; }

    /* 7th input — Cutout Bottom Width (created dynamically; only twoB/three use it) */
    if (inputBT && !document.getElementById('cc-cutout-bw')) {
      var field = document.createElement('div');
      field.className = 'cc-field';
      field.innerHTML = '<label class="cc-fl" for="cc-cutout-bw">Cutout Bottom Width (in) <span class="cc-req">*</span></label>'
        + '<input class="cc-fi" id="cc-cutout-bw" type="number" min="1" max="120" placeholder="e.g. 30" inputmode="decimal">';
      inputBT.closest('.cc-field').parentNode.appendChild(field);
    }

    /* Section labels */
    var seatLabel = document.querySelector('.cc-seat-label');
    if (seatLabel) { seatLabel.style.display = ''; seatLabel.textContent = 'Widths & Thickness'; }
    var backLabel = document.querySelector('.cc-back-label');
    if (backLabel) { backLabel.style.display = ''; backLabel.textContent = 'Length & Cutouts'; }

    /* Hide price area above SVG */
    var spTop = document.querySelector('.cc-sp-top');
    if (spTop) spTop.style.display = 'none';
    var priceBreakDiv = document.getElementById('cc-price-break');
    if (priceBreakDiv) priceBreakDiv.style.display = 'none';

    var dimsLabels = document.querySelectorAll('.cc-dims-label');
    for (var j = 0; j < dimsLabels.length; j++) {
      dimsLabels[j].style.marginBottom = '0';
      dimsLabels[j].style.paddingBottom = '0';
    }

    /* Move dimension inputs out of sticky container */
    var stickyWrap = document.getElementById('cc-sticky-preview');
    if (stickyWrap) {
      var dimsContainer = document.createElement('div');
      dimsContainer.className = 'cc-dims-outside';
      var seatLbl = stickyWrap.querySelector('.cc-seat-label');
      var seatDims = stickyWrap.querySelector('.cc-sp-dims:not(.cc-back-dims)');
      var backLbl = stickyWrap.querySelector('.cc-back-label');
      var backDimsEl = stickyWrap.querySelector('.cc-sp-dims.cc-back-dims');
      var hint = stickyWrap.querySelector('.cc-sp-hint');
      if (seatLbl) dimsContainer.appendChild(seatLbl);
      if (seatDims) dimsContainer.appendChild(seatDims);
      if (backLbl) dimsContainer.appendChild(backLbl);
      if (backDimsEl) dimsContainer.appendChild(backDimsEl);
      if (hint) dimsContainer.appendChild(hint);
      stickyWrap.parentNode.insertBefore(dimsContainer, stickyWrap.nextSibling);
    }

    /* Renumber steps (shape section stays visible) */
    var secHeads = document.querySelectorAll('.cc-sec-head .cc-sn');
    var stepNum = 1;
    for (var k = 0; k < secHeads.length; k++) {
      var sec = secHeads[k].closest('.cc-sec');
      if (sec && sec.style.display !== 'none') secHeads[k].textContent = stepNum++;
    }
  }

  /* ─── Init ─── */
  function init() {
    var wrap = document.getElementById('cc-configurator');
    if (!wrap) return;

    transformDOM();

    els.priceMain    = document.getElementById('cc-price-main');
    els.priceBreak   = document.getElementById('cc-price-break');
    els.summary      = document.getElementById('cc-sp-summary');
    els.topW         = document.getElementById('cc-dim-l');
    els.bottomW      = document.getElementById('cc-dim-w');
    els.thickness    = document.getElementById('cc-dim-t');
    els.totalL       = document.getElementById('cc-back-l');
    els.cutoutLength = document.getElementById('cc-back-w');
    els.cutoutTopWidth = document.getElementById('cc-back-t');
    els.cutoutBottomWidth = document.getElementById('cc-cutout-bw');
    els.extraCovers  = document.getElementById('cc-extra-covers');
    els.throwPillow  = document.getElementById('cc-throw-pillow');
    els.pillowCovers = document.getElementById('cc-pillow-covers');
    els.armrestPillow = document.getElementById('cc-armrest-pillow');
    els.zipperPlacement = document.getElementById('cc-zipper-placement');
    els.pipingColor    = document.getElementById('cc-piping-color');
    els.pipingColorWrap = document.getElementById('cc-piping-color-wrap');
    els.notes        = document.getElementById('cc-notes');
    els.addBtn       = document.getElementById('cc-add-btn');
    els.addBtnSticky = document.getElementById('cc-add-btn-sticky');
    els.addNote      = document.getElementById('cc-add-note');
    els.dimSvg       = document.getElementById('cc-dim-svg');

    wrap.addEventListener('click', function (e) {
      var cardEl = e.target.closest('.cc-opt-card');
      if (!cardEl || !cardEl.dataset.group) return;
      pickCard(cardEl, cardEl.dataset.group, cardEl.dataset.value);
    });

    ['topW', 'bottomW', 'thickness', 'totalL', 'cutoutLength', 'cutoutTopWidth', 'cutoutBottomWidth'].forEach(function (id) {
      if (els[id]) els[id].addEventListener('input', recalc);
    });
    els.extraCovers.addEventListener('change', recalc);
    if (els.throwPillow) els.throwPillow.addEventListener('change', recalc);
    if (els.pillowCovers) els.pillowCovers.addEventListener('change', recalc);
    if (els.armrestPillow) els.armrestPillow.addEventListener('change', recalc);
    if (els.zipperPlacement) els.zipperPlacement.addEventListener('change', recalc);
    if (els.pipingColor) els.pipingColor.addEventListener('change', recalc);
    els.notes.addEventListener('input', recalc);

    document.addEventListener('fabric:selected', function (e) {
      state.fabric = e.detail;
      recalc();
    });
    var existingFabric = window.__ucfs_selectedFabric || window._ucfsSelected;
    if (!state.fabric && existingFabric) {
      state.fabric = existingFabric;
    }
    setInterval(function () {
      var fb = window.__ucfs_selectedFabric || window._ucfsSelected;
      if (fb && fb !== state.fabric) { state.fabric = fb; recalc(); }
    }, 800);

    function handleAdd() {
      if (!isComplete()) return;
      document.dispatchEvent(new CustomEvent('configurator:add-to-cart', { detail: getLineProperties() }));
    }
    if (els.addBtn) els.addBtn.addEventListener('click', handleAdd);
    if (els.addBtnSticky) els.addBtnSticky.addEventListener('click', handleAdd);

    /* default selections */
    var defaultZipper = wrap.querySelector('.cc-opt-card[data-group="zipper"][data-value="regular"]');
    if (defaultZipper) defaultZipper.classList.add('sel');
    var defaultPieces = wrap.querySelector('.cc-opt-card[data-group="pieces"][data-value="one"]');
    if (defaultPieces) defaultPieces.classList.add('sel');
    state.pieces = 'one';

    drawCushion();
    recalc();
  }

  window.VBerthCushionConfigurator = {
    getState: function () { return state; },
    getLineProperties: getLineProperties,
    recalc: recalc
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();